import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../config";

export default function AdminDashboard() {
  const token = localStorage.getItem("token");

  const [orders,   setOrders]   = useState([]);
  const [products, setProducts] = useState([]);
  const [visitors, setVisitors] = useState(0);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!token) { window.location.href = "/login"; return; }

    const load = async () => {
      try {
        const [ordRes, proRes] = await Promise.all([
          axios.get(`${API_URL}/api/orders`,   { headers: { Authorization: token } }),
          axios.get(`${API_URL}/api/products`),
        ]);
        setOrders(ordRes.data);
        setProducts(proRes.data);

        try {
          const visRes = await axios.get(`${API_URL}/api/visitors`, { headers: { Authorization: token } });
          setVisitors(visRes.data.count || 0);
        } catch { setVisitors("N/A"); }

      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (!token) return null;

  // ── Computed stats ──────────────────────────────────────────────────────────
  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);
  const pending      = orders.filter(o => o.status === "pending").length;
  const processing   = orders.filter(o => o.status === "processing").length;
  const shipped      = orders.filter(o => o.status === "shipped").length;
  const delivered    = orders.filter(o => o.status === "delivered").length;

  const productSales = {};
  orders.forEach(o => {
    (o.items || []).forEach(item => {
      productSales[item.name] = (productSales[item.name] || 0) + (item.quantity || 1);
    });
  });
  const topProducts = Object.entries(productSales)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const now  = Date.now();
  const week = 7 * 24 * 60 * 60 * 1000;
  const recentOrders = orders.filter(o => now - new Date(o.createdAt).getTime() < week).length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .ad-root*,.ad-root *::before,.ad-root *::after{box-sizing:border-box;margin:0;padding:0}
        .ad-root{font-family:'DM Sans',sans-serif;background:#faf8f6;min-height:100vh;padding:28px 28px 64px;max-width:1100px;margin:0 auto}
        @media(max-width:600px){.ad-root{padding:16px 12px 48px}}

        .ad-header{margin-bottom:28px}
        .ad-eyebrow{font-size:9px;font-weight:500;letter-spacing:.26em;text-transform:uppercase;color:#c87090;display:block;margin-bottom:5px}
        .ad-title{font-family:'Cormorant Garamond',serif;font-size:clamp(22px,4vw,32px);font-weight:300;color:#1a1410;display:block;letter-spacing:-.01em}

        /* STAT CARDS */
        .ad-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
        @media(max-width:900px){.ad-stats{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:480px){.ad-stats{grid-template-columns:repeat(2,1fr);gap:8px}}
        .ad-stat{background:#fff;border:1px solid #ede8e2;padding:16px 18px}
        .ad-stat-val{font-family:'Cormorant Garamond',serif;font-size:clamp(24px,4vw,36px);font-weight:300;color:#1a1410;display:block;line-height:1;margin-bottom:4px}
        .ad-stat-label{font-size:10px;font-weight:400;letter-spacing:.14em;text-transform:uppercase;color:#a09890;display:block}
        .ad-stat-accent{border-top:2px solid #c87090}
        .ad-stat-green {border-top:2px solid #10b981}
        .ad-stat-blue  {border-top:2px solid #3b82f6}
        .ad-stat-amber {border-top:2px solid #f59e0b}

        /* STATUS ROW */
        .ad-status-row{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:24px}
        @media(max-width:700px){.ad-status-row{grid-template-columns:repeat(2,1fr)}}
        .ad-status-card{background:#fff;border:1px solid #ede8e2;padding:12px 14px;display:flex;align-items:center;gap:10px}
        .ad-status-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}
        .ad-s-pending   {background:#fbbf24}
        .ad-s-processing{background:#f97316}
        .ad-s-shipped   {background:#3b82f6}
        .ad-s-delivered {background:#10b981}
        .ad-status-num{font-size:20px;font-weight:500;color:#1a1410;display:block;line-height:1}
        .ad-status-name{font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#a09890}

        /* PANELS */
        .ad-panels{display:grid;grid-template-columns:1fr 1fr;gap:16px}
        @media(max-width:700px){.ad-panels{grid-template-columns:1fr}}
        .ad-panel{background:#fff;border:1px solid #ede8e2;padding:18px 20px}
        .ad-panel-title{font-family:'Cormorant Garamond',serif;font-size:18px;font-weight:300;color:#1a1410;display:block;margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid #f0ebe5}
        .ad-top-row{display:flex;align-items:center;justify-content:space-between;padding:7px 0;border-bottom:1px solid #f7f4f1}
        .ad-top-row:last-child{border-bottom:none}
        .ad-top-name{font-size:13px;font-weight:400;color:#1a1410}
        .ad-top-qty{font-size:12px;font-weight:500;color:#c87090;background:#fff5f8;padding:2px 8px;border-radius:10px}
        .ad-recent-row{display:flex;align-items:flex-start;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f7f4f1;gap:8px}
        .ad-recent-row:last-child{border-bottom:none}
        .ad-recent-name{font-size:13px;font-weight:400;color:#1a1410;display:block}
        .ad-recent-meta{font-size:11px;font-weight:300;color:#a09890;display:block;margin-top:1px}
        .ad-recent-total{font-size:12px;font-weight:500;color:#1a1410;white-space:nowrap}
        .ad-recent-badge{display:inline-block;font-size:8.5px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;padding:2px 7px;border-radius:10px;margin-top:3px}
        .ad-rb-pending   {background:#fef3c7;color:#92400e}
        .ad-rb-processing{background:#ffedd5;color:#9a3412}
        .ad-rb-shipped   {background:#dbeafe;color:#1e40af}
        .ad-rb-delivered {background:#d1fae5;color:#065f46}

        .ad-loading{text-align:center;padding:60px;color:#a09890;font-size:14px;font-weight:300}
        .ad-nav-link{display:inline-flex;align-items:center;gap:6px;margin-top:20px;font-size:10px;font-weight:500;letter-spacing:.14em;text-transform:uppercase;color:#a09890;text-decoration:none;border-bottom:1px solid #ddd8d2;padding-bottom:2px;transition:color .18s,border-color .18s}
        .ad-nav-link:hover{color:#c87090;border-color:#c87090}

        /* LOGOUT BUTTON */
        .ad-logout{
          display:inline-flex;align-items:center;gap:6px;
          background:none;border:1px solid #ede8e2;
          font-size:11px;font-weight:500;letter-spacing:.1em;text-transform:uppercase;
          color:#a09890;cursor:pointer;padding:6px 14px;
          font-family:'DM Sans',sans-serif;
          transition:all .18s;
        }
        .ad-logout:hover{color:#c87090;border-color:#c87090}
      `}</style>

      <div className="ad-root">

        <div className="ad-header" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
          <div>
            <span className="ad-eyebrow">Admin Panel</span>
            <span className="ad-title">Dashboard</span>
          </div>
          <button
            className="ad-logout"
            onClick={() => {
              localStorage.removeItem("token");
              window.location.href = "/login";
            }}
          >
            ⎋ Logout
          </button>
        </div>

        {loading ? (
          <div className="ad-loading">Loading dashboard…</div>
        ) : (
          <>
            {/* TOP STATS */}
            <div className="ad-stats">
              <div className="ad-stat ad-stat-accent">
                <span className="ad-stat-val">{orders.length}</span>
                <span className="ad-stat-label">Total Orders</span>
              </div>
              <div className="ad-stat ad-stat-green">
                <span className="ad-stat-val">
                  {totalRevenue >= 1000
                    ? (totalRevenue / 1000).toFixed(1) + "K"
                    : totalRevenue.toLocaleString()}
                </span>
                <span className="ad-stat-label">Revenue (PKR)</span>
              </div>
              <div className="ad-stat ad-stat-blue">
                <span className="ad-stat-val">{products.length}</span>
                <span className="ad-stat-label">Products Listed</span>
              </div>
              <div className="ad-stat ad-stat-amber">
                <span className="ad-stat-val">
                  {typeof visitors === "number" ? visitors.toLocaleString() : visitors}
                </span>
                <span className="ad-stat-label">Site Visitors</span>
              </div>
            </div>

            {/* STATUS BREAKDOWN */}
            <div className="ad-status-row">
              {[
                { label:"Pending",    count:pending,    dot:"ad-s-pending"    },
                { label:"Processing", count:processing, dot:"ad-s-processing" },
                { label:"Shipped",    count:shipped,    dot:"ad-s-shipped"    },
                { label:"Delivered",  count:delivered,  dot:"ad-s-delivered"  },
              ].map(s => (
                <div key={s.label} className="ad-status-card">
                  <span className={`ad-status-dot ${s.dot}`} />
                  <div className="ad-status-info">
                    <span className="ad-status-num">{s.count}</span>
                    <span className="ad-status-name">{s.label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* PANELS */}
            <div className="ad-panels">

              <div className="ad-panel">
                <span className="ad-panel-title">Top Selling Products</span>
                {topProducts.length === 0 ? (
                  <p style={{fontSize:13,color:"#a09890",fontWeight:300}}>No sales data yet.</p>
                ) : (
                  topProducts.map(([name, qty]) => (
                    <div key={name} className="ad-top-row">
                      <span className="ad-top-name">{name}</span>
                      <span className="ad-top-qty">{qty} sold</span>
                    </div>
                  ))
                )}
              </div>

              <div className="ad-panel">
                <span className="ad-panel-title">
                  Recent Orders
                  <span style={{fontSize:11,color:"#a09890",fontWeight:300,marginLeft:8}}>
                    ({recentOrders} this week)
                  </span>
                </span>
                {orders.slice(0, 6).map(o => {
                  const badgeClass =
                    o.status === "pending"    ? "ad-rb-pending"    :
                    o.status === "processing" ? "ad-rb-processing" :
                    o.status === "shipped"    ? "ad-rb-shipped"    :
                    "ad-rb-delivered";
                  return (
                    <div key={o._id} className="ad-recent-row">
                      <div>
                        <span className="ad-recent-name">{o.customerName}</span>
                        <span className="ad-recent-meta">{o.phone}</span>
                        <span className={`ad-recent-badge ${badgeClass}`}>{o.status}</span>
                      </div>
                      <span className="ad-recent-total">{o.total?.toLocaleString()} PKR</span>
                    </div>
                  );
                })}
              </div>

            </div>

            {/* Nav links */}
            <div style={{display:"flex",gap:20,flexWrap:"wrap",marginTop:24}}>
              <a href="/admin"        className="ad-nav-link">← Manage Products</a>
              <a href="/admin/orders" className="ad-nav-link">Manage Orders →</a>
            </div>
          </>
        )}
      </div>
    </>
  );
}